import type {
  NodeAlertsApi,
  NodeAlertApi,
  NodeAlert,
  NodeAlerts,
} from 'src/types/node-alert';
import type {FilterBlock, FilterRule, PreviousRule} from 'src/types/filter';

import {uniqueId} from 'lodash';

import {
  normalizeFilter,
  parseFilterBlocks,
  validateFilterBlocks,
} from 'src/utils/filter';
import {splitNodeIdEvent} from 'src/components/lib/automation-variable-picker/useAutomationPicker';


const TRIGGER_TYPE_CONFIGS = [
  {
    trigger_type: 'survey_responded_conditional',
    contains_pattern: 'children.survey_trigger_conversation_fetch',
  },
  {
    trigger_type: 'sms_reply',
    contains_pattern: 'sms_replied',
  },
  {
    trigger_type: 'email_opened',
    contains_pattern: 'email_opened_v2',
  },
  {
    trigger_type: 'email_bounced',
    contains_pattern: 'email_bounced_v2',
  },
  {
    trigger_type: 'email_skipped',
    contains_pattern: 'email_skipped_v2',
  },
  {
    trigger_type: 'survey_responded',
    contains_pattern: 'survey_responded',
  },
  {
    trigger_type: 'conversation_completed',
    contains_pattern: 'conversation_completed',
  },
];

const TEMPLATE_NAMES = {
  email_opened: 'Email Open Notification',
  sms_reply: 'SMS Reply Notification',
  survey_responded: 'Survey Response Received',
  survey_responded_conditional: 'Survey Alert - Conditional',
  email_skipped: 'Email Delivery Skipped Notification',
  email_bounced: 'Email Bounced Notification',
  conversation_completed: 'Chatbot Completion Notification',
};

export const normalizeNodeAlerts = (
  alerts: Array<NodeAlert>,
): {alerts: Array<NodeAlertApi>} => {
  const alertsApi = alerts.map((alert): NodeAlertApi => {
    if (!alert.to) {
      throw new Error('Alert recipient is required');
    }
    return {
      id: alert.id.includes('alert') ? null : alert.id,
      template_name: alert.template_name ?? null,
      template_id: alert.template_id ?? null,
      ...normalizeFilter({blocks: alert.blocks}),
      to: alert.to,
      response_key: alert.response_key,
    };
  });
  return {alerts: alertsApi};
};

export const parseNodeAlerts = (payload: NodeAlertsApi): NodeAlerts => {
  const alerts = payload.alerts ?? [];

  return {
    alerts: alerts.map((alert): NodeAlert => {
      return {
        id: alert.id ? '' + alert.id : uniqueId('alert'),
        template_name: alert.template_name,
        template_id: alert.template_id,
        blocks: parseFilterBlocks(alert.conditions?.blocks),
        to: alert.to,
        response_key: alert.response_key,
      };
    }),
  };
};

/**
 * Resolves the trigger type (alert name) from the given conditions using config-driven pattern matching.
 * @param {object} conditions - Condition tree (composite with blocks, rules, conditions)
 * @returns {string} The trigger type string
 * @throws {Error} If no pattern matches or input invalid
 */
export function resolveAlertTriggerType(alert: NodeAlert) {
  const fields = extractAllFields(alert.blocks);
  if (fields.length === 0) {
    throw new Error('No condition fields found');
  }

  const matchedConfig = TRIGGER_TYPE_CONFIGS.find((config) => {
    const pattern = config.contains_pattern;
    return (
      pattern &&
      typeof pattern === 'string' &&
      pattern.trim() !== '' &&
      fields.some((field) => field.includes(pattern))
    );
  });

  if (matchedConfig) {
    return matchedConfig.trigger_type;
  }

  throw new Error(
    `No trigger type matched. Fields checked: ${JSON.stringify(fields)}`,
  );
}

function extractAllFields(blocks: Map<string, FilterBlock>) {
  if (!blocks) {
    return [];
  }
  const fields: Array<string> = [];
  for (const block of blocks.values()) {
    if (!block.rules || !Array.isArray(block.rules)) {
      continue;
    }
    for (const rule of block.rules) {
      if (!rule) {
        continue;
      }
      if (rule.type === 'ems') {
        continue;
      }
      if (rule.type === 'reqRes') {
        continue;
      }
      for (const condition of (rule as PreviousRule).conditions ?? []) {
        if (
          condition &&
          typeof condition.field === 'string' &&
          condition.field.trim() !== ''
        ) {
          fields.push(condition.field);
        }
      }
    }
  }
  return fields;
}

export const getDefaultTemplateName = (triggerType: string) => {
  return TEMPLATE_NAMES[triggerType as keyof typeof TEMPLATE_NAMES];
};

/**NOTE:(diwakersurya) This config is maintained for handling the searching of abt rule on which
 * a req res rule depends so that we can automatically insert the abt rule for the dependent
 * req res event.
 */
const dependentSelections = {
  survey_trigger_conversation_fetch: [
    {
      recipientActionRelationSearchString: 'survey_v2',
      abtEventSearchString: 'survey_responded',
    },
  ],
};

export type PreviousNodeVariable = {
  nodeId: string;
  nodeLabel: string;
  abtEvents: Array<string>;
};

type ReqResItem = {
  id: string;
  nodeId: string;
  moduleKey: string;
};

/**
 * For a reqRes variable, return any ABT events that must be included first.
 */
export function getPrerequisiteABTEvents(
  reqResItem: ReqResItem,
  previousNodeVariables: PreviousNodeVariable[],
  recipientActionRelationNamesArr: Array<string>,
): Array<{
  nodeId: string;
  id: string;
  moduleKey: string;
  prevNodeVariableType: 'abt';
  value: boolean;
}> {
  const {id, nodeId, moduleKey} = reqResItem;
  const allAbtEvents =
    previousNodeVariables.find((variable) => variable.nodeId === nodeId)
      ?.abtEvents ?? [];
  const requiredABTEvents = Object.keys(dependentSelections).reduce(
    (acc, key) => {
      if (id.includes(key)) {
        dependentSelections[key as keyof typeof dependentSelections].forEach(
          ({recipientActionRelationSearchString, abtEventSearchString}) => {
            //find the partial dependent identifier in the recipientActionRelationNamesArr
            const dependentIdentifierFromRecipientActionRelationNamesArr =
              recipientActionRelationNamesArr.find((relationName) =>
                relationName.includes(recipientActionRelationSearchString),
              );
            if (dependentIdentifierFromRecipientActionRelationNamesArr) {
              const dependentEvent = allAbtEvents.find(
                (event) =>
                  event.includes(
                    dependentIdentifierFromRecipientActionRelationNamesArr,
                  ) &&
                  event.includes(moduleKey) &&
                  event.includes(abtEventSearchString),
              );
              if (dependentEvent) {
                acc.push(dependentEvent);
              }
            }
          },
        );
      }
      return acc;
    },
    [] as Array<string>,
  );
  return requiredABTEvents.map((event) => {
    const {
      nodeId: prerequisiteNodeId,
      event: prerequisiteEvent,
      moduleKey,
    } = splitNodeIdEvent(event);
    return {
      nodeId: prerequisiteNodeId,
      id: prerequisiteEvent,
      moduleKey,
      prevNodeVariableType: 'abt',
      value: true,
    };
  });
}

export function getDependencyIdentifiersForReqResField(reqResField: string): {
  recipientActionRelationSearchString: string;
  abtEventSearchString: string;
}[] {
  const dependencyIdentifiers: Array<{
    recipientActionRelationSearchString: string;
    abtEventSearchString: string;
  }> = [];
  Object.keys(dependentSelections).forEach((key) => {
    if (reqResField.includes(key)) {
      dependencyIdentifiers.push(
        ...dependentSelections[key as keyof typeof dependentSelections],
      );
    }
  });
  return dependencyIdentifiers;
}

export function getAutoInsertedDependencyRuleIdsForReqResRule(
  rules: Array<FilterRule>,
  reqResRuleIndex: number,
): string[] {
  const reqResRule = rules[reqResRuleIndex];
  if (reqResRule?.type !== 'reqRes') {
    return [];
  }
  const reqResField = reqResRule.field;
  if (!reqResField) {
    return [];
  }

  const dependencyIdentifiers =
    getDependencyIdentifiersForReqResField(reqResField);
  if (dependencyIdentifiers.length === 0) {
    return [];
  }

  const ids: Array<string> = [];
  let found = 0;
  for (
    let j = reqResRuleIndex - 1;
    j >= 0 && found < dependencyIdentifiers.length;
    j--
  ) {
    const maybePrevNodeRule = rules[j];
    if (maybePrevNodeRule?.type !== 'prevNode') {
      continue;
    }
    const conditions = maybePrevNodeRule.conditions;
    const matchesDependency = dependencyIdentifiers.some(
      ({recipientActionRelationSearchString, abtEventSearchString}) =>
        conditions.some(
          (condition) =>
            condition.field.includes(recipientActionRelationSearchString) &&
            condition.field.includes(abtEventSearchString),
        ),
    );
    if (!matchesDependency) {
      continue;
    }

    const id = maybePrevNodeRule.id;
    if (typeof id === 'string') {
      ids.push(id);
    }
    found += 1;
  }
  return ids;
}

export function getInvisibleRuleIndexes(rules: Array<FilterRule>): number[] {
  if (!Array.isArray(rules) || rules.length === 0) {
    return [];
  }

  const invisible = new Set<number>();
  rules.forEach((rule, i) => {
    if (rule?.type !== 'reqRes') {
      return;
    }

    const reqResField = rule.field;
    if (!reqResField) {
      return;
    }

    const dependencyIdentifiers =
      getDependencyIdentifiersForReqResField(reqResField);
    if (dependencyIdentifiers.length === 0) {
      return;
    }

    let found = 0;
    for (let j = i - 1; j >= 0 && found < dependencyIdentifiers.length; j--) {
      const maybePrevNodeRule = rules[j];
      if (maybePrevNodeRule?.type !== 'prevNode') {
        continue;
      }
      const conditions = maybePrevNodeRule.conditions;
      const matchesDependency = dependencyIdentifiers.some(
        ({recipientActionRelationSearchString, abtEventSearchString}) =>
          conditions.some(
            (condition) =>
              condition.field.includes(recipientActionRelationSearchString) &&
              condition.field.includes(abtEventSearchString),
          ),
      );

      if (matchesDependency) {
        invisible.add(j);
        found += 1;
      }
    }
  });

  return Array.from(invisible).sort((a, b) => a - b);
}

type GetAbtPickerVariableInfo = Parameters<typeof validateFilterBlocks>[2];
type GetAbtUtilVariableInfo = Parameters<typeof validateFilterBlocks>[3];

export const getAlertsValidationState = (
  alerts: Array<NodeAlert>,
  getAbtPickerVariableInfo: GetAbtPickerVariableInfo,
  getAbtUtilVariableInfo: GetAbtUtilVariableInfo,
): {
  invalidRuleExistsInAnyBlock: boolean;
  alertsWithNoRecipient: Array<string>;
} => {
  if (alerts.length === 0) {
    return {
      invalidRuleExistsInAnyBlock: false,
      alertsWithNoRecipient: [],
    };
  }

  let anyInvalid = false;
  const alertsWithNoRecipient: Array<string> = [];

  for (const alert of alerts) {
    const res = validateFilterBlocks(
      alert.blocks,
      1,
      getAbtPickerVariableInfo,
      getAbtUtilVariableInfo,
    );
    anyInvalid = anyInvalid || res.invalidRuleExistsInAnyBlock;
    if (alert.to == null) {
      alertsWithNoRecipient.push(alert.id);
    }
  }

  return {
    invalidRuleExistsInAnyBlock: anyInvalid,
    alertsWithNoRecipient,
  };
};
